import { useContext, useState } from 'react'

import classNames from 'classnames'
import { useField } from 'formik'
import { find, get, invert, keyBy, map, mapValues, omit } from 'lodash'
import Xarrow, { Xwrapper } from 'react-xarrows'

import CoverImage from 'Root/App/CoverImage'
import PageLinkWrapper from 'Root/App/Quiz/PageLinkWrapper'
import { Context as StateContext } from 'Root/App/Quiz/StateContext'
import { isMatchingCompletedWithSuccess } from 'Root/App/Quiz/utilities'
import Title from 'Root/App/Title'

import FailureFeedback from '../../Feedback/Failure'
import SuccessFeedback from '../../Feedback/Success'
import DraggableCell from './DraggableCell'
import styles from './index.module.css'

interface Props {
  question: any
}

interface CurrentProps {
  type: 'subquestion' | 'subanswer'
  value: string
}

const Matching = ({ question }: Props) => {
  const {
    state: { answers, content },
  } = useContext(StateContext)
  const answer = get(answers, question.id)

  const [current, setCurrent] = useState<undefined | CurrentProps>()
  const [_field, meta, helpers] = useField(`answers.${question.id}`)

  const { value } = meta
  const { setValue, setTouched } = helpers

  const hasAnswered = Boolean(get(answers, question.id))

  const originalMatchPairs = find(
    content.questions,
    ({ __typename, id }) =>
      __typename === 'QuizContentQuestionMatching' && id === question.id
  ) as any

  const originalMatches = mapValues(
    keyBy(originalMatchPairs?.matchPairs, 'subquestion'),
    'subanswer'
  )

  const hasAnsweredCorrectly =
    hasAnswered && isMatchingCompletedWithSuccess(content, question, answer)

  const displayValidation = content.showAnswer && hasAnswered

  const displaySuccessFeedback =
    hasAnsweredCorrectly && Boolean(question.successFeedback)
  const displayFailureFeedback =
    hasAnswered && !hasAnsweredCorrectly && Boolean(question.failureFeedback)

  const onClick = (selected: CurrentProps) => () => {
    // if the user is selecting a question that was already selected
    // than we should remove the selection
    if (selected.type === 'subquestion' && Boolean(value?.[selected.value])) {
      setValue(omit(value, [selected.value]))
      setTouched(true)

      return
    }

    if (
      selected.type === 'subanswer' &&
      Boolean(invert(value || {})?.[selected.value])
    ) {
      const subquestionValue = invert(value || {})?.[selected.value]
      setValue(omit(value, [subquestionValue]))
      setTouched(true)

      return
    }

    if (typeof current === 'undefined' || selected.type === current?.type) {
      setCurrent(selected)

      return
    }

    const subquestion = selected.type === 'subquestion' ? selected : current
    const subanswer = selected.type === 'subanswer' ? selected : current

    setValue({
      ...(value || {}),
      [subquestion.value]: subanswer.value,
    })
    setCurrent(undefined)
    setTouched(true)
  }

  const isRightSubquestion = (value) =>
    (answer || {})?.[value] === originalMatches[value]

  const isRightSubanswer = (value) =>
    invert(answer || {})?.[value] === invert(originalMatches)[value]

  return (
    <div className="flex flex-col gap-y-3 mb-4">
      <PageLinkWrapper slug={content.slug}>
        <Title>{question.title}</Title>
      </PageLinkWrapper>

      {Boolean(question.image) && <CoverImage imageUrl={question.image} />}

      <div className="flex flex-col gap-3">
        {displaySuccessFeedback && (
          <SuccessFeedback>{question.successFeedback}</SuccessFeedback>
        )}
        {displayFailureFeedback && (
          <FailureFeedback>{question.failureFeedback}</FailureFeedback>
        )}
        <div>
          <Xwrapper>
            <div className="flex flex-col gap-y-7">
              {question.matchPairs.map(({ subanswer, subquestion }, index) => (
                <div className={styles.row} key={index}>
                  <DraggableCell
                    className={classNames(styles.cell, {
                      [styles.cellActive]:
                        !displayValidation &&
                        'subquestion' === current?.type &&
                        subquestion === current?.value,
                      [styles.cellSelected]:
                        !displayValidation && Boolean(value?.[subquestion]),
                      [styles.success]:
                        displayValidation && isRightSubquestion(subquestion),
                      [styles.failure]:
                        displayValidation && !isRightSubquestion(subquestion),
                    })}
                    id={`cell-subquestion-${subquestion}`}
                    onClick={onClick({
                      type: 'subquestion',
                      value: subquestion,
                    })}
                  >
                    {subquestion}
                  </DraggableCell>
                  <DraggableCell
                    className={classNames(styles.cell, {
                      [styles.cellActive]:
                        !displayValidation &&
                        'subanswer' === current?.type &&
                        subanswer === current?.value,
                      [styles.cellSelected]:
                        !displayValidation &&
                        Boolean(invert(value || {})?.[subanswer]),
                      [styles.success]:
                        displayValidation && isRightSubanswer(subanswer),
                      [styles.failure]:
                        displayValidation && !isRightSubanswer(subanswer),
                    })}
                    id={`cell-subanswer-${subanswer}`}
                    onClick={onClick({
                      type: 'subanswer',
                      value: subanswer,
                    })}
                  >
                    {subanswer}
                  </DraggableCell>
                </div>
              ))}
              {map(value || {}, (subanswer, subquestion) => (
                <Xarrow
                  key={`arrow-from-${subquestion}-to-${subanswer}`}
                  end={`cell-subanswer-${subanswer}`}
                  start={`cell-subquestion-${subquestion}`}
                  headSize={4}
                  strokeWidth={2}
                />
              ))}
            </div>
          </Xwrapper>
        </div>
      </div>
    </div>
  )
}

export default Matching
